/**
 * File: businessRecordUtils.ts
 * Path: apps/admin/src/shared/utils/businessRecordUtils.ts
 * Description: Pure rules for the one-record-per-business layout (data tidy,
 * Liam 3 Nov 2026). Until the move has reached every record, the lists are the
 * union of `businesses/{id}` and the old `leads` and `clients`: an old document
 * is skipped when a business already stands for it, so nobody shows twice.
 * Also finds possible duplicates (same phone). The merge itself is the
 * `mergeBusinesses` callable.
 * Nothing here reads or writes the database.
 */

import {
  CLIENTS_LIST_LIMIT,
  legacyIdsOf,
  LEADS_PIPELINE_LIMIT,
  normalisePhone,
  toClient,
  toLead,
  type Client,
  type ClientType,
  type Lead,
} from "@idk/config";

export type Row = Record<string, unknown> & { id: string };

/** The stages that belong on the Clients page; every other stage is the Pipeline's. */
const CLIENT_SIDE_STAGES = ["won", "past"] as const;

export const isClientSideStage = (stage: unknown): boolean =>
  CLIENT_SIDE_STAGES.some((s) => s === stage);

const asObject = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};

const idText = (value: unknown): string => (typeof value === "string" ? value : "");

export { legacyIdsOf };

const shadowedBy = (businesses: readonly Row[]): Set<string> =>
  new Set(businesses.flatMap(legacyIdsOf));

/** The Client a business shows as: its stage decides the type. */
export const clientTypeOf = (row: Row): ClientType =>
  row.stage === "past" ? "past" : row.partner === true ? "partner" : "client";

export const businessToLead = (row: Row): Lead => toLead(row.id, row);

export const businessToClient = (row: Row): Client => {
  const legacy = asObject(row.legacy);
  return toClient(row.id, {
    ...row,
    type: clientTypeOf(row),
    leadId: idText(legacy.leadId) || null,
  });
};

const byRecentFirst = <T extends { updatedAt: number | null }>(a: T, b: T) =>
  (b.updatedAt ?? 0) - (a.updatedAt ?? 0);

/**
 * The Pipeline's rows: businesses before "won", then every old lead no
 * business stands for (those keep whatever stage they have).
 */
export const leadsUnion = (businesses: readonly Row[], oldLeads: readonly Row[]): Lead[] => {
  const shadowed = shadowedBy(businesses);
  return [
    ...businesses.filter((b) => !isClientSideStage(b.stage)).map(businessToLead),
    ...oldLeads.filter((l) => !shadowed.has(l.id)).map((l) => toLead(l.id, l)),
  ]
    .sort(byRecentFirst)
    .slice(0, LEADS_PIPELINE_LIMIT);
};

/** The Clients page's rows: won and past businesses, then old clients nothing stands for. */
export const clientsUnion = (businesses: readonly Row[], oldClients: readonly Row[]): Client[] => {
  const shadowed = shadowedBy(businesses);
  return [
    ...businesses.filter((b) => isClientSideStage(b.stage)).map(businessToClient),
    ...oldClients.filter((c) => !shadowed.has(c.id)).map((c) => toClient(c.id, c)),
  ]
    .sort(byRecentFirst)
    .slice(0, CLIENTS_LIST_LIMIT);
};

/** What changing the Clients-page type does to a business record. */
export const businessFieldsForType = (type: ClientType): { stage: string; partner: boolean } => {
  switch (type) {
    case "past":
      return { stage: "past", partner: false };
    case "partner":
      return { stage: "won", partner: true };
    case "lead":
      return { stage: "new", partner: false };
    default:
      return { stage: "won", partner: false };
  }
};

// ─── Possible duplicates ───────────────────────────────────────────────────

const checkedIds = (row: Row): string[] =>
  Array.isArray(row.notDuplicateOf) ? row.notDuplicateOf.map(idText).filter(Boolean) : [];

/** True when either record has the other marked "Not the same". */
const markedNotSame = (a: Row, b: Row): boolean =>
  checkedIds(a).includes(b.id) || checkedIds(b).includes(a.id);

export interface DuplicateOf {
  id: string;
  name: string;
  phone: string;
}

/** Other businesses with the same phone number that nobody has marked as different. */
export const duplicatesOf = (target: Row, all: readonly Row[]): DuplicateOf[] => {
  const phone = normalisePhone(idText(target.phone));
  if (!phone) return [];
  return all
    .filter((other) => other.id !== target.id && normalisePhone(idText(other.phone)) === phone)
    .filter((other) => !markedNotSame(target, other))
    .map((other) => ({ id: other.id, name: idText(other.name) || "(no name)", phone: idText(other.phone) }));
};
